/**
 * UserPerformanceSearch Component
 * Search interface to find and view any user's performance (admin only)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { Card } from '../../common';
import { Search, User, X, Loader2 } from 'lucide-react';
import { useUserPerformance } from '../../../hooks';
import type { PerformanceQueryParams } from '../../../types';
import { getScoreColor, getScoreCategory } from '../../../types';

export interface UserSearchResult {
  id: string;
  name: string;
  email: string;
  department?: string;
  role?: string;
}

export interface UserPerformanceSearchProps {
  onSearch: (query: string) => Promise<UserSearchResult[]>;
  queryParams: PerformanceQueryParams;
  className?: string;
}

const CATEGORY_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  needsImprovement: 'Needs Improvement',
  poor: 'Poor',
};

export const UserPerformanceSearch: React.FC<UserPerformanceSearchProps> = ({
  onSearch,
  queryParams,
  className,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  // Fetch selected user's performance
  const { data: userPerformance, isLoading: isLoadingPerformance } = useUserPerformance(
    selectedUserId || '',
    queryParams,
    !!selectedUserId
  );

  // The debounce used to return its cleanup from an event handler, where nothing calls it:
  // every keystroke fired a request, and a slow early answer could overwrite a later one.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRequestRef = useRef(0);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleSearch = useCallback(async (query: string) => {
    const requestId = ++latestRequestRef.current;
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await onSearch(query);
      if (requestId !== latestRequestRef.current) return; // a newer query superseded this one
      setSearchResults(results);
      setShowResults(true);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      if (requestId === latestRequestRef.current) setIsSearching(false);
    }
  }, [onSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUserId(user.id);
    setSearchQuery(user.name);
    setShowResults(false);
  };

  const handleClearSelection = () => {
    // Drop any pending or in-flight search, or its results would reappear after clearing.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    latestRequestRef.current++;
    setIsSearching(false);
    setShowResults(false);
    setSelectedUserId(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // selectedUser can be used for displaying selected user info in future enhancements
  // const selectedUser = searchResults.find((u) => u.id === selectedUserId) ||
  //   (selectedUserId && userPerformance ? {
  //     id: userPerformance.userId,
  //     name: userPerformance.userName,
  //     email: userPerformance.userEmail,
  //   } : null);

  return (
    <Card className={clsx('p-6', className)}>
      <h3 className="text-lg font-semibold text-stone-800 mb-4">Search User Performance</h3>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          className="w-full pl-10 pr-10 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {(searchQuery || selectedUserId) && (
          <button
            onClick={handleClearSelection}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-stone-100 rounded"
          >
            <X className="w-4 h-4 text-stone-400" />
          </button>
        )}
        {isSearching && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 animate-spin" />
        )}

        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {searchResults.map((user) => (
              <button
                key={user.id}
                onClick={() => handleSelectUser(user)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-stone-50 text-left border-b border-stone-100 last:border-b-0"
              >
                <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-stone-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-800 truncate">{user.name}</p>
                  <p className="text-xs text-stone-500 truncate">{user.email}</p>
                </div>
                {user.department && (
                  <span className="text-xs text-stone-400">{user.department}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg p-4 text-center text-stone-500">
            No users found matching "{searchQuery}"
          </div>
        )}
      </div>

      {/* Selected User Performance */}
      {selectedUserId && (
        <div className="mt-6">
          {isLoadingPerformance ? (
            <div className="animate-pulse">
              <div className="h-32 bg-stone-100 rounded-lg" />
            </div>
          ) : userPerformance ? (
            <div className="border border-stone-200 rounded-lg p-4">
              {/* User Header */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-stone-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-stone-800">{userPerformance.userName}</h4>
                  <p className="text-sm text-stone-500">{userPerformance.userEmail}</p>
                </div>
                <div className="text-right">
                  <span
                    className="text-3xl font-bold"
                    style={{ color: getScoreColor(userPerformance.score.overall) }}
                  >
                    {Math.round(userPerformance.score.overall)}
                  </span>
                  <p className="text-xs text-stone-500">Overall Score</p>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-stone-50 rounded p-2 text-center">
                  <p
                    className="text-lg font-bold"
                    style={{ color: getScoreColor(userPerformance.score.okrProgress) }}
                  >
                    {Math.round(userPerformance.score.okrProgress)}%
                  </p>
                  <p className="text-xs text-stone-500">OKR Progress</p>
                </div>
                <div className="bg-stone-50 rounded p-2 text-center">
                  <p
                    className="text-lg font-bold"
                    style={{ color: getScoreColor(userPerformance.score.checkInCompliance) }}
                  >
                    {Math.round(userPerformance.score.checkInCompliance)}%
                  </p>
                  <p className="text-xs text-stone-500">Check-ins</p>
                </div>
                <div className="bg-stone-50 rounded p-2 text-center">
                  <p
                    className="text-lg font-bold"
                    style={{ color: getScoreColor(userPerformance.score.engagement) }}
                  >
                    {Math.round(userPerformance.score.engagement)}
                  </p>
                  <p className="text-xs text-stone-500">Engagement</p>
                </div>
                <div className="bg-stone-50 rounded p-2 text-center">
                  <p
                    className="text-lg font-bold"
                    style={{ color: getScoreColor(userPerformance.score.timeliness) }}
                  >
                    {Math.round(userPerformance.score.timeliness)}
                  </p>
                  <p className="text-xs text-stone-500">Timeliness</p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-500">Performance Category</span>
                <span
                  className={clsx(
                    'px-3 py-1 rounded-full text-sm font-medium',
                    getScoreCategory(userPerformance.score.overall) === 'excellent' && 'bg-green-100 text-green-700',
                    getScoreCategory(userPerformance.score.overall) === 'good' && 'bg-blue-100 text-blue-700',
                    getScoreCategory(userPerformance.score.overall) === 'needsImprovement' && 'bg-amber-100 text-amber-700',
                    getScoreCategory(userPerformance.score.overall) === 'poor' && 'bg-red-100 text-red-700'
                  )}
                >
                  {CATEGORY_LABELS[getScoreCategory(userPerformance.score.overall)]}
                </span>
              </div>

              {/* OKR Count */}
              {userPerformance.okrs && (
                <div className="mt-3 pt-3 border-t border-stone-200">
                  <span className="text-sm text-stone-500">
                    {userPerformance.okrs.length} OKRs in this period
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-stone-400 border border-stone-200 rounded-lg">
              <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No performance data available for this user</p>
            </div>
          )}
        </div>
      )}

      {/* Helper Text */}
      {!selectedUserId && (
        <p className="mt-4 text-xs text-stone-400">
          Search for any user in the organization to view their performance metrics.
        </p>
      )}
    </Card>
  );
};

export default UserPerformanceSearch;
